import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Separator } from './ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import CategoryDropdown from './ui/CategoryDropdown';
import { inventoryAPI } from '../services/api';
import SKUPillInput from './ui/SKUPillInput';
import { cn } from '../lib/utils';
import { Plus, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SuccessScreen from './ui/SuccessScreen';
import BeautifiedErrorAlert from './ui/BeautifiedErrorAlert';
import useServerValidation from '../hooks/useServerValidation';
import FormInput from './ui/FormInput';

const EditProductModal = ({ product, onClose, onUpdate }) => {
    const [formData, setFormData] = useState({
        categoryId: '',
        categoryName: '',
        productName: '',
        brand: '',
        productType: '', // Add productType
        hsnCode: '',
        purchasePriceInr: '',
        sellingPriceInr: '',
        stockQty: '',
        warrantyMonths: '',
        gstPercent: '',
        dealerSupplierName: '',
        purchaseDate: '',
        skuProductCodes: [],
        warrantyStartDate: '',
        warrantyEndDate: '',
        description: '',
        specification: '',
        locationRack: ''
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [categories, setCategories] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(false);
    const [categoriesPage, setCategoriesPage] = useState(0);
    const [hasMoreCategories, setHasMoreCategories] = useState(true);
    
    const [showInlineCategoryModal, setShowInlineCategoryModal] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [isAddingCategory, setIsAddingCategory] = useState(false);
    const [inlineCategorySuccess, setInlineCategorySuccess] = useState('');
    const [inlineCategoryError, setInlineCategoryError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const navigate = useNavigate();
    const [submitError, setSubmitError] = useState(null);
    const [isSuccess, setIsSuccess] = useState(false);
    const [countdown, setCountdown] = useState(3);
    const [isFormValidating, setIsFormValidating] = useState(false);
    const { applyServerErrors } = useServerValidation({ setErrors });

    useEffect(() => {
        let timer;
        if (isSuccess && countdown > 0) {
            timer = setInterval(() => setCountdown(c => c - 1), 1000);
        } else if (isSuccess && countdown === 0) {
            onClose();
            navigate('/admin-dashboard');
        }
        return () => clearInterval(timer);
    }, [isSuccess, countdown, navigate, onClose]);

    useEffect(() => {
        if (product) {
            setFormData({
                categoryId: product.categoryId || '',
                categoryName: product.categoryName || '',
                productName: product.productName || product.name || '',
                brand: product.brand || '',
                productType: product.productType || 'SERIALIZED', // Default to SERIALIZED if missing
                hsnCode: product.hsnCode || '',
                purchasePriceInr: product.purchasePriceInr || '',
                sellingPriceInr: product.sellingPriceInr || product.price || '',
                warrantyMonths: product.warrantyMonths || '',
                gstPercent: product.gstPercent || '',
                dealerSupplierName: product.dealerSupplierName || '',
                purchaseDate: product.purchaseDate || '',
                skuProductCodes: Array.isArray(product.skuProductCodes) ? product.skuProductCodes : (product.skuProductCode ? [product.skuProductCode] : []),
                stockQty: Array.isArray(product.skuProductCodes) ? product.skuProductCodes.length : (product.skuProductCode ? 1 : 0),
                warrantyStartDate: product.warrantyStartDate || '',
                warrantyEndDate: product.warrantyEndDate || '',
                description: product.description || '',
                specification: product.specification || '',
                locationRack: product.locationRack || ''
            });
        }
    }, [product]);

    useEffect(() => {
        const fetchCategories = async (page = 0, reset = false) => {
            if (categoriesLoading && !reset) return;
            setCategoriesLoading(true);
            try {
                const params = {
                    pageNumber: page,
                    pageSize: 10,
                    sortBy: 'categoryName',
                    sortOrder: 'ASC'
                };
                const response = await inventoryAPI.getCategories(params);
                const newCategories = response.data.content || response.data;
                const isLast = response.data.lastPage || response.data.last || newCategories.length < 10;
                setCategories(prev => reset ? newCategories : [...prev, ...newCategories]);
                setHasMoreCategories(!isLast);
                setCategoriesPage(page);
            } catch (error) {
                console.error('Error fetching categories:', error);
                setCategories([]);
            } finally {
                setCategoriesLoading(false);
            }
        };
        fetchCategories(0, true);
    }, []);

    const loadMoreCategories = useCallback(() => {
        if (!categoriesLoading && hasMoreCategories) {
            const nextPage = categoriesPage + 1;
            setCategoriesLoading(true);
            const params = {
                pageNumber: nextPage,
                pageSize: 10,
                sortBy: 'categoryName',
                sortOrder: 'ASC'
            };
            inventoryAPI.getCategories(params).then(response => {
                const newCategories = response.data.content || response.data;
                const isLast = response.data.lastPage || response.data.last || newCategories.length < 10;
                setCategories(prev => [...prev, ...newCategories]);
                setHasMoreCategories(!isLast);
                setCategoriesPage(nextPage);
            }).catch(error => {
                console.error('Error fetching more categories:', error);
            }).finally(() => {
                setCategoriesLoading(false);
            });
        }
    }, [categoriesLoading, hasMoreCategories, categoriesPage]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const newData = { ...prev, [name]: value };

            if (name === 'warrantyStartDate' || name === 'warrantyEndDate') {
                const start = name === 'warrantyStartDate' ? value : newData.warrantyStartDate;
                const end = name === 'warrantyEndDate' ? value : newData.warrantyEndDate;

                if (start && end) {
                    const startDate = new Date(start);
                    const endDate = new Date(end);
                    if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
                        let months = (endDate.getFullYear() - startDate.getFullYear()) * 12;
                        months -= startDate.getMonth();
                        months += endDate.getMonth();
                        if (endDate.getDate() < startDate.getDate()) {
                            months--;
                        }
                        newData.warrantyMonths = Math.max(0, months);
                    }
                } else if (name === 'warrantyEndDate' && !newData.warrantyStartDate) {
                    const today = new Date().toISOString().split('T')[0];
                    newData.warrantyStartDate = today;
                    const startDate = new Date(today);
                    const endDate = new Date(value);
                    if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
                        let months = (endDate.getFullYear() - startDate.getFullYear()) * 12;
                        months -= startDate.getMonth();
                        months += endDate.getMonth();
                        if (endDate.getDate() < startDate.getDate()) {
                            months--;
                        }
                        newData.warrantyMonths = Math.max(0, months);
                    }
                }
            }
            return newData;
        });
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.productName.trim()) {
            newErrors.productName = 'Product name is required';
        }

        if (!formData.brand.trim()) {
            newErrors.brand = 'Brand is required';
        }

        if (formData.productType !== 'SERVICE') {
            if (formData.stockQty === '' || formData.stockQty < 0) {
                newErrors.stockQty = 'Valid stock quantity is required';
            } else if (formData.productType === 'NON_SERIAL' && formData.stockQty <= 0) {
                newErrors.stockQty = 'Quantity must be greater than 0';
            }
        }

        // Mandatory Fields (All Product Types)
        if (!formData.categoryId) newErrors.categoryId = 'Category is required';
        if (!formData.purchaseDate) newErrors.purchaseDate = 'Purchase Date is required';

        // Mandatory for SERIALIZED only
        if (formData.productType === 'SERIALIZED') {
            if (!formData.warrantyStartDate) {
                newErrors.warrantyStartDate = 'Warranty start date is required';
            }
            if (!formData.warrantyEndDate) {
                newErrors.warrantyEndDate = 'Warranty end date is required';
            }
            if (!formData.skuProductCodes || formData.skuProductCodes.length === 0) {
                newErrors.skuProductCodes = 'At least one SKU is required for serialized products';
            }
        }

        // Optional fields: description, specification (no validation needed)



        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError(null);
        setIsFormValidating(true);

        if (!validateForm()) {
            setSubmitError('Please check the highlighted fields above; the request cannot proceed.');
            setIsFormValidating(false);
            return;
        }

        setIsSubmitting(true);

        try {
            const updatedProduct = {
                ...formData,
                categoryId: formData.categoryId ? parseInt(formData.categoryId) : 0,
                purchasePriceInr: parseFloat(formData.purchasePriceInr) || 0,
                sellingPriceInr: parseFloat(formData.sellingPriceInr) || 0,
                stockQty: parseInt(formData.stockQty) || 0,
                warrantyMonths: parseInt(formData.warrantyMonths) || 0,
                gstPercent: parseFloat(formData.gstPercent) || 0
            };

            await onUpdate(updatedProduct);
            setIsSuccess(true);
        } catch (error) {
            console.error('Error updating product:', error);
            applyServerErrors(error);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to update product. Please try again.';
            setSubmitError(errorMessage);
        } finally {
            setIsSubmitting(false);
            setIsFormValidating(false);
        }
    };

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent className={cn(
                "overflow-hidden flex flex-col p-0 transition-all duration-500 rounded-[3rem] border-0 shadow-2xl ring-1 ring-white/20 dark:bg-slate-950 dark:ring-white/10 dark:text-slate-100",
                isSuccess ? "max-w-md max-h-[500px]" : "max-w-[1100px] max-h-[98vh]"
            )}>
              {isSuccess ? (
                <SuccessScreen 
                    message="Product Updated!"
                    subMessage="Returning to inventory list..."
                    countdown={countdown}
                    onAction={() => setCountdown(0)}
                />
              ) : (
                <>
                <DialogHeader className="p-8 pb-4 border-b bg-white/40 dark:bg-slate-900/60 dark:border-slate-800 backdrop-blur-3xl sticky top-0 z-40">
                    <div className="flex items-center gap-5">
                        <div className="relative group">
                            <div className="absolute -inset-1 bg-gradient-to-r from-[#FF7D00] to-amber-500 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
                            <div className="relative p-3.5 rounded-2xl bg-[#FF7D00]/10 text-[#FF7D00] shadow-sm transform hover:scale-105 transition-transform duration-300">
                                <Plus className="w-8 h-8 rotate-45" />
                            </div>
                        </div>
                        <div>
                            <DialogTitle className="text-3xl font-black tracking-tight text-slate-800 dark:text-white flex items-center gap-2">
                                Inventory Precision
                                <span className="text-sm font-black px-3 py-1 bg-[#FF7D00]/10 text-[#FF7D00] rounded-full uppercase tracking-widest ml-1 shadow-sm">Editor</span>
                            </DialogTitle>
                            <DialogDescription className="text-sm font-bold text-slate-500 dark:text-slate-400 mt-1 opacity-80">
                                Refining specifications, pricing, and stock for the JIT system.
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="flex-1 overflow-hidden flex flex-col pt-0">
                    <div className="flex-1 overflow-y-auto px-6 py-6 scroll-smooth pt-4">
                        {submitError && (
                            <div className="mb-6 animate-in slide-in-from-top-4 duration-300">
                                <BeautifiedErrorAlert error={submitError} onDismiss={() => setSubmitError(null)} />
                            </div>
                        )}

                    {/* Basic Info Section */}
                    <div className="space-y-6 mb-10">
                        <div className="flex items-center gap-2 px-1">
                            <div className="w-1.5 h-6 bg-indigo-500 rounded-full" />
                            <h3 className="text-sm font-black uppercase tracking-widest text-slate-400">Core DNA</h3>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <FormInput
                                id="productName"
                                name="productName"
                                label={<span className="text-[11px] font-black uppercase tracking-wider text-[#FF7D00] ml-1">Product Title *</span>}
                                value={formData.productName}
                                onChange={handleChange}
                                error={errors.productName}
                                className={cn("jit-input", errors.productName && 'bg-red-50/50 dark:bg-red-500/10 animate-shake')}
                                placeholder="e.g. Dell Latitude 7420"
                            />

                            <div className="space-y-2">
                                <Label htmlFor="brand" className="text-[11px] font-black uppercase tracking-wider text-slate-500 ml-1">Brand Identity *</Label>
                                <Input
                                    id="brand"
                                    name="brand"
                                    value={formData.brand}
                                    onChange={handleChange}
                                    className={cn("h-12 rounded-xl border-slate-200 focus:ring-2 focus:ring-indigo-500/20 transition-all font-bold", errors.brand && 'border-rose-400 bg-rose-50/30')}
                                    placeholder="Enter brand name"
                                />
                                {errors.brand && (
                                    <p className="text-[10px] text-rose-500 font-bold ml-1 uppercase">{errors.brand}</p>
                                )}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="productType" className="text-[11px] font-black uppercase tracking-wider text-slate-500 ml-1">Classification *</Label>
                                <Select
                                    value={formData.productType}
                                    onValueChange={(value) => {
                                        setFormData(prev => ({
                                            ...prev,
                                            productType: value,
                                            skuProductCodes: value === 'SERIALIZED' ? prev.skuProductCodes : [],
                                            stockQty: value === 'SERIALIZED' ? prev.skuProductCodes.length : (value === 'SERVICE' ? 0 : prev.stockQty)
                                        }));
                                    }}
                                >
                                    <SelectTrigger className="h-12 rounded-xl border-slate-200 font-bold">
                                        <SelectValue placeholder="Select Type" />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-xl border-slate-200 shadow-2xl">
                                        <SelectItem value="SERIALIZED">📦 Serialized</SelectItem>
                                        <SelectItem value="NON_SERIAL">🛒 Non-Serialized</SelectItem>
                                        <SelectItem value="SERVICE">🛠️ Service</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>


                        <div className="space-y-2">
                            <Label htmlFor="categoryId" className="text-[11px] font-black uppercase tracking-wider text-slate-500 ml-1">Category Registry *</Label>
                            <div className="flex gap-2">
                                <div className="flex-1">
                                    <CategoryDropdown
                                        value={formData.categoryId}
                                        onChange={(id, name) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                categoryId: id,
                                                categoryName: name
                                            }));
                                            if (errors.categoryId) setErrors(prev => ({ ...prev, categoryId: '' }));
                                        }}
                                        placeholder="Select a category"
                                        categories={categories}
                                        loading={categoriesLoading}
                                        hasMore={hasMoreCategories}
                                        onLoadMore={loadMoreCategories}
                                        error={errors.categoryId}
                                    />
                                </div>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="icon"
                                    className="flex-shrink-0 h-10 w-10 bg-indigo-50 border-indigo-100 text-indigo-600 hover:bg-indigo-100 rounded-xl"
                                    onClick={() => setShowInlineCategoryModal(true)}
                                    title="Add New Category"
                                >
                                    <Plus className="h-5 w-5" />
                                </Button>
                            </div>
                            {errors.categoryId && (
                                <p className="text-[10px] text-rose-500 font-bold ml-1 uppercase">{errors.categoryId}</p>
                            )}
                        </div>

                        {/* Conditional SKU Input */}
                        {formData.productType === 'SERIALIZED' && (
                            <div className="space-y-2">
                                <Label className="text-[11px] font-black uppercase tracking-wider text-slate-500 ml-1">SKU Genome / Serial Codes *</Label>
                                <SKUPillInput
                                    value={formData.skuProductCodes}
                                    onChange={(skus) => {
                                        setFormData(prev => ({ ...prev, skuProductCodes: skus, stockQty: skus.length }));
                                        if (errors.skuProductCodes) setErrors(prev => ({ ...prev, skuProductCodes: '' }));
                                    }}
                                    placeholder="Enter SKUs..."
                                    error={errors.skuProductCodes}
                                />
                                {errors.skuProductCodes && (
                                    <p className="text-[10px] text-rose-500 font-bold ml-1 uppercase">{errors.skuProductCodes}</p>
                                )}
                            </div>
                        )}

                             <div className="space-y-2">
                                <Label htmlFor="hsnCode" className="text-[11px] font-black uppercase tracking-wider text-slate-500 ml-1">HSN Code <span className="text-slate-300 font-medium normal-case">(optional)</span></Label>
                                <Input
                                    id="hsnCode"
                                    name="hsnCode"
                                    value={formData.hsnCode}
                                    onChange={handleChange}
                                    className="h-12 rounded-xl border-slate-200 focus:ring-2 focus:ring-indigo-500/20 transition-all font-bold uppercase"
                                    placeholder="8-digit HSN (optional)"
                                />
                            </div>
                        
                    <Separator />

                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold">Pricing & Stock</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="purchasePriceInr" className="text-[11px] font-black uppercase tracking-wider text-slate-500 ml-1">Purchase Price (₹)</Label>
                                <Input
                                    id="purchasePriceInr"
                                    name="purchasePriceInr"
                                    type="number"
                                    value={formData.purchasePriceInr}
                                    onChange={handleChange}
                                    step="0.01"
                                    min="0"
                                    placeholder="0.00"
                                    className="h-12 rounded-xl border-slate-200 focus:ring-2 focus:ring-indigo-500/20 transition-all font-bold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="sellingPriceInr" className="text-[11px] font-black uppercase tracking-wider text-slate-500 ml-1">Selling Price (₹)</Label>
                                <Input
                                    id="sellingPriceInr"
                                    name="sellingPriceInr"
                                    type="number"
                                    value={formData.sellingPriceInr}
                                    onChange={handleChange}
                                    step="0.01"
                                    min="0"
                                    placeholder="0.00"
                                    className="h-12 rounded-xl border-slate-200 focus:ring-2 focus:ring-indigo-500/20 transition-all font-bold text-emerald-600 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                            </div>

                            <div className="space-y-2">
                                {formData.productType !== 'SERVICE' && (
                                    <>
                                        <Label htmlFor="stockQty" className="text-[11px] font-black uppercase tracking-wider text-slate-500 ml-1">Stock Quantity *</Label>
                                        <Input
                                            id="stockQty"
                                            name="stockQty"
                                            type="number"
                                            value={formData.stockQty}
                                            onChange={(e) => {
                                                if (formData.productType === 'NON_SERIAL') {
                                                    handleChange(e);
                                                }
                                            }}
                                            readOnly={formData.productType === 'SERIALIZED'}
                                            className={cn(
                                                "h-12 rounded-xl border-slate-200 focus:ring-2 focus:ring-indigo-500/20 transition-all font-bold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                                formData.productType === 'SERIALIZED' ? "bg-slate-50 opacity-60 cursor-not-allowed" : "",
                                                errors.stockQty && 'border-rose-400 bg-rose-50/30'
                                            )}
                                            min="0"
                                            placeholder="0"
                                        />
                                        {errors.stockQty && (
                                            <p className="text-[10px] text-rose-500 font-bold ml-1 uppercase">{errors.stockQty}</p>
                                        )}
                                    </>
                                )}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="locationRack" className="text-[11px] font-black uppercase tracking-wider text-slate-500 ml-1">Location/Rack</Label>
                                <Input
                                    id="locationRack"
                                    name="locationRack"
                                    value={formData.locationRack}
                                    onChange={handleChange}
                                    className={errors.locationRack ? 'border-destructive' : ''}
                                    placeholder="e.g., Rack A-12"
                                />
                                {errors.locationRack && (
                                    <p className="text-sm text-destructive">{errors.locationRack}</p>
                                )}
                            </div>
                        </div>
                    </div>

                    <Separator />

                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold">Supplier & Purchase Details</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="dealerSupplierName" className="text-[11px] font-black uppercase tracking-wider text-slate-500 ml-1">Dealer/Supplier Name</Label>
                                <Input
                                    id="dealerSupplierName"
                                    name="dealerSupplierName"
                                    value={formData.dealerSupplierName}
                                    onChange={handleChange}
                                    placeholder="Enter supplier name"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="purchaseDate" className="text-[11px] font-black uppercase tracking-wider text-slate-500 ml-1">Purchase Date *</Label>
                                <Input
                                    id="purchaseDate"
                                    name="purchaseDate"
                                    type="date"
                                    value={formData.purchaseDate}
                                    onChange={handleChange}
                                    className={cn("h-12 rounded-xl border-slate-200 focus:ring-2 focus:ring-indigo-500/20 transition-all font-bold", errors.purchaseDate && 'border-rose-400 bg-rose-50/30')}
                                />
                                {errors.purchaseDate && (
                                    <p className="text-[10px] text-rose-500 font-bold ml-1 uppercase">{errors.purchaseDate}</p>
                                )}
                            </div>
                        </div>
                    </div>

                    <Separator />

                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold">Warranty Information</h3>
                        <div className="grid grid-cols-2 gap-4">

                            <div className="space-y-2">
                                <Label htmlFor="gstPercent" className="text-[11px] font-black uppercase tracking-wider text-slate-500 ml-1">GST Percent (%)</Label>
                                <Input
                                    id="gstPercent"
                                    name="gstPercent"
                                    type="number"
                                    value={formData.gstPercent}
                                    onChange={handleChange}
                                    min="0"
                                    max="100"
                                    step="0.01"
                                    placeholder="0.00"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="warrantyStartDate" className="text-[11px] font-black uppercase tracking-wider text-slate-500 ml-1">Warranty Start Date {formData.productType === 'SERIALIZED' ? '*' : ''}</Label>
                                <Input
                                    id="warrantyStartDate"
                                    name="warrantyStartDate"
                                    type="date"
                                    value={formData.warrantyStartDate}
                                    onChange={handleChange}
                                    className={cn("h-12 rounded-xl border-slate-200 focus:ring-2 focus:ring-indigo-500/20 transition-all font-bold", errors.warrantyStartDate && 'border-rose-400 bg-rose-50/30')}
                                />
                                {errors.warrantyStartDate && (
                                    <p className="text-[10px] text-rose-500 font-bold ml-1 uppercase">{errors.warrantyStartDate}</p>
                                )}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="warrantyEndDate" className="text-[11px] font-black uppercase tracking-wider text-slate-500 ml-1">Warranty End Date {formData.productType === 'SERIALIZED' ? '*' : ''}</Label>
                                <Input
                                    id="warrantyEndDate"
                                    name="warrantyEndDate"
                                    type="date"
                                    value={formData.warrantyEndDate}
                                    onChange={handleChange}
                                    className={cn("h-12 rounded-xl border-slate-200 focus:ring-2 focus:ring-indigo-500/20 transition-all font-bold", errors.warrantyEndDate && 'border-rose-400 bg-rose-50/30')}
                                />
                                {errors.warrantyEndDate && (
                                    <p className="text-[10px] text-rose-500 font-bold ml-1 uppercase">{errors.warrantyEndDate}</p>
                                )}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="warrantyMonths" className="text-[11px] font-black uppercase tracking-wider text-slate-500 ml-1 text-indigo-600">Total Months</Label>
                                <Input
                                    id="warrantyMonths"
                                    name="warrantyMonths"
                                    value={formData.warrantyMonths}
                                    readOnly
                                    className="bg-slate-50 font-black text-indigo-600 h-12"
                                />
                            </div>
                        </div>
                    </div>

                    <Separator />

                    {/* Description Section */}
                    <div className="space-y-6 mb-10">
                        <div className="flex items-center gap-2 px-1">
                            <div className="w-1.5 h-6 bg-slate-400 rounded-full" />
                            <h3 className="text-sm font-black uppercase tracking-widest text-slate-400">Documentation</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <Label htmlFor="description" className="text-[11px] font-black uppercase tracking-wider text-slate-500 ml-1">Product Description</Label>
                                <Textarea
                                    id="description"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    rows="4"
                                    className="rounded-2xl border-slate-200 focus:ring-2 focus:ring-indigo-500/20 transition-all font-medium resize-none p-4"
                                    placeholder="Detailed description of the product..."
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="specification" className="text-[11px] font-black uppercase tracking-wider text-slate-500 ml-1">Technical Specs</Label>
                                <Textarea
                                    id="specification"
                                    name="specification"
                                    value={formData.specification}
                                    onChange={handleChange}
                                    rows="4"
                                    className="rounded-2xl border-slate-200 focus:ring-2 focus:ring-indigo-500/20 transition-all font-medium resize-none p-4"
                                    placeholder="RAM, Processor, Storage, etc."
                                />
                            </div>
                        </div>
                    </div>
                    </div>

                    <DialogFooter className="p-6 pt-4 border-t bg-slate-50/50 dark:bg-slate-900/60 dark:border-slate-800 sticky bottom-0 z-30 sm:justify-end items-center gap-4 shadow-[0_-4px_20px_-5px_rgba(0,0,0,0.05)]">
                        <div className="flex gap-4 w-full sm:w-auto">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={onClose}
                                disabled={isSubmitting}
                                className="flex-1 sm:flex-none h-12 px-8 rounded-xl border-slate-200 dark:border-slate-800 font-black text-slate-500 dark:text-slate-400"
                            >
                                Discard
                            </Button>
                            <Button
                                type="submit"
                                disabled={isSubmitting}
                                className="flex-1 sm:flex-none h-12 px-12 rounded-xl bg-[#FF7D00] hover:brightness-110 active:scale-95 text-white font-black uppercase tracking-widest shadow-xl shadow-orange-500/10 transition-all duration-200"
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                        Updating...
                                    </>
                                ) : (
                                    'Save Changes'
                                )}
                            </Button>
                        </div>
                    </DialogFooter>
                </form>


                {/* Inline Category Addition Modal */}
                <Dialog open={showInlineCategoryModal} onOpenChange={setShowInlineCategoryModal}>
                    <DialogContent className="max-w-[450px] rounded-[3rem] border-0 shadow-2xl ring-1 ring-white/20 p-0 overflow-hidden">
                        <DialogHeader className="p-8 pb-4 border-b bg-white/40 backdrop-blur-3xl">
                            <div className="flex items-center gap-4">
                                <div className="p-3 rounded-2xl bg-emerald-100 text-emerald-600 shadow-sm">
                                    <Plus className="w-6 h-6" />
                                </div>
                                <div>
                                    <DialogTitle className="text-xl font-black tracking-tight text-slate-800 uppercase">New Category</DialogTitle>
                                    <DialogDescription className="text-xs font-bold text-slate-500 mt-1 opacity-70 italic font-medium">Create a new category for your inventory.</DialogDescription>
                                </div>
                            </div>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label htmlFor="newCategoryName">Category Name *</Label>
                                <Input
                                    id="newCategoryName"
                                    value={newCategoryName}
                                    onChange={(e) => setNewCategoryName(e.target.value)}
                                    placeholder="Enter category name"
                                    autoFocus
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowInlineCategoryModal(false)}
                                disabled={isAddingCategory}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                className="bg-sky-500 hover:bg-sky-600"
                                disabled={isAddingCategory || !newCategoryName.trim()}
                                onClick={async () => {
                                    try {
                                        setInlineCategoryError('');
                                        setIsAddingCategory(true);
                                        const response = await inventoryAPI.createCategory({ categoryName: newCategoryName });
                                        const newCat = response.data;

                                        setInlineCategorySuccess("Category created successfully!");

                                        // Reload list
                                        const reloadCategories = async () => {
                                            const params = { pageNumber: 0, pageSize: 10, sortBy: 'categoryName', sortOrder: 'ASC' };
                                            const res = await inventoryAPI.getCategories(params);
                                            setCategories(res.data.content || res.data);
                                            setHasMoreCategories(!(res.data.lastPage || res.data.last || (res.data.content || res.data).length < 10));
                                            setCategoriesPage(0);
                                        };
                                        await reloadCategories();

                                        // Select the new category
                                        setFormData(prev => ({
                                            ...prev,
                                            categoryId: newCat.categoryId,
                                            categoryName: newCat.categoryName
                                        }));

                                        setTimeout(() => {
                                            setNewCategoryName('');
                                            setShowInlineCategoryModal(false);
                                            setInlineCategorySuccess('');
                                        }, 1500);
                                    } catch (err) {
                                        console.error("Failed to add category inline", err);
                                        let errorMessage = err.response?.data?.message || err.message || "Failed to create category";
                                        
                                        // Helper to parse complex Java validation messages
                                        if (errorMessage.includes("ConstraintViolationImpl")) {
                                            const match = errorMessage.match(/interpolatedMessage='([^']*)'/);
                                            if (match && match[1]) {
                                                errorMessage = match[1];
                                            }
                                        }
                                        
                                        setInlineCategoryError(errorMessage);
                                    } finally {
                                        setIsAddingCategory(false);
                                    }
                                }}
                            >
                                {isAddingCategory ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Saving...
                                    </>
                                ) : 'Save Category'}
                            </Button>
                        </DialogFooter>
                        <div className="px-6 pb-4">
                            {inlineCategorySuccess && (
                                <div className="p-2 text-sm text-emerald-600 bg-emerald-50 border border-emerald-100 rounded text-center animate-in fade-in zoom-in-95">
                                    {inlineCategorySuccess}
                                </div>
                            )}
                            {inlineCategoryError && (
                                <div className="p-3 text-[11px] text-rose-600 bg-rose-50 border border-rose-100 rounded-lg text-left break-words overflow-y-auto max-h-[160px] animate-in slide-in-from-bottom-2">
                                    <div className="font-bold mb-1 flex items-center gap-1.5 uppercase tracking-tight">
                                        <AlertCircle className="h-3 w-3" /> Oh no! Something went wrong
                                    </div>
                                    {inlineCategoryError}
                                </div>
                            )}
                        </div>
                    </DialogContent>
                </Dialog>
                </>
              )}
            </DialogContent>
        </Dialog>
    );
};

export default EditProductModal;
